import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, Zap, Users, Building2 } from 'lucide-react';
import { Button } from './ui';

const tiers = [
  {
    name: 'Pay As You Go',
    slug: 'payg',
    icon: Zap,
    price: '£10',
    priceDetail: ' to get started',
    description: 'Low-commitment on-ramp for individual builders and light usage.',
    highlight: false,
    cta: 'Get started',
    ctaLink: '/signup',
    topUp: '£10 includes £5 platform fee + £5 credits. Further top-ups from £10 (all credits). Credits roll over monthly.',
    features: [
      { text: 'AI workflow orchestration', included: true },
      { text: 'Template library', included: true },
      { text: 'Up to 5 min runtime per workflow', included: true },
      { text: '2 concurrent workflows', included: true },
      { text: 'Standard model access', included: true },
      { text: '1 GB storage', included: true },
      { text: 'Community support', included: true },
      { text: 'Custom tools', included: false },
      { text: 'Save workflows as templates', included: false },
      { text: 'API access', included: false },
      { text: 'Team members', included: false },
    ],
  },
  {
    name: 'Pro',
    slug: 'pro',
    icon: ArrowRight,
    price: '£29',
    priceDetail: '/mo',
    description: 'For power users. Includes compute credits, advanced models, and custom tools.',
    highlight: true,
    cta: 'Upgrade to Pro',  /* product name preserved */
    ctaLink: '/signup',
    topUp: 'Compute credits included. Additional top-ups at any time.',
    features: [
      { text: 'Everything in PAYG, plus:', included: true, bold: true },
      { text: 'Up to 15 min runtime per workflow', included: true },
      { text: 'Compute credits included monthly', included: true },
      { text: '5 concurrent workflows', included: true },
      { text: 'Advanced model access', included: true },
      { text: 'Custom tools (limited)', included: true },
      { text: 'Save workflows as templates', included: true },
      { text: 'Bring your own API keys', included: true },
      { text: '5 GB storage', included: true },
      { text: 'Standard support', included: true },
    ],
  },
  {
    name: 'Team',
    slug: 'team',
    icon: Users,
    price: '£99',
    priceDetail: '/mo per user',
    description: 'For teams. Shared workspaces, role-based access, and premium models. Includes compute credits per seat.',
    highlight: false,
    cta: 'Contact sales',
    ctaLink: 'mailto:hello@flowize.xyz',
    topUp: 'Compute credits included per seat. Additional top-ups at any time.',
    features: [
      { text: 'Everything in Pro, plus:', included: true, bold: true },
      { text: 'Up to 30 min runtime per workflow', included: true },
      { text: 'Compute credits per seat included', included: true },
      { text: '15 concurrent workflows', included: true },
      { text: 'Premium model access', included: true },
      { text: 'Up to 10 team members', included: true },
      { text: 'Shared workspaces', included: true },
      { text: 'Role-based access control', included: true },
      { text: 'API access (optional)', included: true },
      { text: '20 GB storage', included: true },
      { text: 'Priority support', included: true },
    ],
  },
];

export function PricingPage() {
  return (
    <div className="min-h-dvh bg-app-bg py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-semibold text-gray-900 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Start with Pay As You Go — upgrade when you need more power.
            All plans include access to AI workflow orchestration.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            return (
              <div
                key={tier.slug}
                className={`rounded-2xl p-8 flex flex-col relative transition-shadow ${
                  tier.highlight
                    ? 'bg-app-header text-white shadow-2xl shadow-indigo-900/20'
                    : 'bg-white text-gray-900 border border-gray-200/70 shadow-sm'
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold tracking-wide uppercase px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <Icon size={22} className={tier.highlight ? 'text-indigo-300' : 'text-indigo-600'} />
                  <h2 className="text-2xl font-semibold">{tier.name}</h2>
                </div>

                <div className="mb-2 flex items-baseline gap-1.5">
                  <span className="text-4xl font-semibold tabular-nums">{tier.price}</span>
                  <span className={`text-base tabular-nums ${tier.highlight ? 'text-gray-400' : 'text-gray-500'}`}>
                    {tier.priceDetail}
                  </span>
                </div>

                <p className={`text-sm mb-4 ${tier.highlight ? 'text-gray-400' : 'text-gray-500'}`}>
                  {tier.topUp}
                </p>

                <p className={`mb-6 text-sm ${tier.highlight ? 'text-gray-300' : 'text-gray-600'}`}>
                  {tier.description}
                </p>

                {tier.ctaLink.startsWith('mailto:') ? (
                  <Button
                    asChild
                    variant={tier.highlight ? 'secondary' : 'primary'}
                    size="lg"
                    className={`mb-8 w-full ${tier.highlight ? 'border-white/20 bg-white text-app-header hover:bg-gray-100' : ''}`}
                  >
                    <a href={tier.ctaLink}>{tier.cta}</a>
                  </Button>
                ) : (
                  <Button
                    asChild
                    variant={tier.highlight ? 'secondary' : 'primary'}
                    size="lg"
                    className={`mb-8 w-full ${tier.highlight ? 'border-white/20 bg-white text-app-header hover:bg-gray-100' : ''}`}
                  >
                    <Link to={tier.ctaLink}>{tier.cta}</Link>
                  </Button>
                )}

                <ul className="space-y-3 flex-1">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check size={16} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${tier.highlight ? 'text-indigo-300' : 'text-indigo-600'}`} />
                      ) : (
                        <X size={16} className={`mt-0.5 shrink-0 ${tier.highlight ? 'text-gray-600' : 'text-gray-300'}`} />
                      )}
                      <span className={`text-sm ${
                        feature.bold ? 'font-semibold' : ''
                      } ${
                        !feature.included ? (tier.highlight ? 'text-gray-500' : 'text-gray-400') : ''
                      }`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Enterprise CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 bg-white rounded-xl border border-gray-200/70 shadow-sm px-8 py-6">
            <Building2 size={28} className="text-gray-700" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">Enterprise customer?</h3>
              <p className="text-gray-600">
                Custom pricing, unlimited everything, on-prem deployment, SLA.{' '}
                <a href="mailto:enterprise@flowize.xyz" className="text-indigo-600 font-medium hover:text-indigo-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-sm">
                  Contact us: enterprise@flowize.xyz
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Credit explanation */}
        <div className="mt-12 max-w-2xl mx-auto text-center">
          <p className="text-sm text-gray-500">
            Credits are consumed based on the AI models and tools used in your workflows.
            Unused credits roll over monthly.
            See our <Link to="/docs/pricing" className="text-indigo-600 hover:text-indigo-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-sm">pricing documentation</Link> for
            full details on usage costs and billing.
          </p>
        </div>

        {/* Legal links */}
        <div className="mt-8 text-center space-x-6">
          <Link to="/terms" className="text-gray-400 hover:text-gray-600 text-sm">Terms &amp; Conditions</Link>
          <Link to="/privacy" className="text-gray-400 hover:text-gray-600 text-sm">Privacy Policy</Link>
        </div>

        {/* Back to home */}
        <div className="mt-4 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">
            &larr; Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
